import { useEffect } from "react";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin } from "lucide-react";

interface City {
  slug: string;
  name: string;
  relationship: string;
}

interface NearbyCitiesProps {
  currentCitySlug: string;
  serviceSlug?: string;
  serviceName?: string;
}

// City relationships - organized by proximity and importance
const cityRelationships: Record<string, City[]> = {
  "santa-barbara": [
    { slug: "montecito", name: "Montecito", relationship: "5 miles east" },
    { slug: "goleta", name: "Goleta", relationship: "10 miles west" },
    { slug: "carpinteria", name: "Carpinteria", relationship: "12 miles southeast" }
  ],
  "montecito": [
    { slug: "santa-barbara", name: "Santa Barbara", relationship: "5 miles west" },
    { slug: "carpinteria", name: "Carpinteria", relationship: "8 miles southeast" },
    { slug: "goleta", name: "Goleta", relationship: "15 miles west" }
  ],
  "goleta": [
    { slug: "santa-barbara", name: "Santa Barbara", relationship: "10 miles east" },
    { slug: "isla-vista", name: "Isla Vista", relationship: "3 miles west" },
    { slug: "montecito", name: "Montecito", relationship: "15 miles east" }
  ],
  "carpinteria": [
    { slug: "montecito", name: "Montecito", relationship: "8 miles northwest" },
    { slug: "summerland", name: "Summerland", relationship: "3 miles northwest" },
    { slug: "ventura", name: "Ventura", relationship: "15 miles southeast" }
  ],
  "ventura": [
    { slug: "oxnard", name: "Oxnard", relationship: "8 miles southeast" },
    { slug: "ojai", name: "Ojai", relationship: "14 miles northeast" },
    { slug: "camarillo", name: "Camarillo", relationship: "12 miles southeast" }
  ],
  "ojai": [
    { slug: "ventura", name: "Ventura", relationship: "14 miles southwest" },
    { slug: "santa-barbara", name: "Santa Barbara", relationship: "35 miles northwest" },
    { slug: "camarillo", name: "Camarillo", relationship: "20 miles south" }
  ],
  "camarillo": [
    { slug: "ventura", name: "Ventura", relationship: "12 miles northwest" },
    { slug: "oxnard", name: "Oxnard", relationship: "8 miles southwest" },
    { slug: "ojai", name: "Ojai", relationship: "20 miles north" }
  ],
  "solvang": [
    { slug: "buellton", name: "Buellton", relationship: "3 miles west" },
    { slug: "santa-ynez", name: "Santa Ynez", relationship: "5 miles east" },
    { slug: "santa-barbara", name: "Santa Barbara", relationship: "35 miles southeast" }
  ],
  "buellton": [
    { slug: "solvang", name: "Solvang", relationship: "3 miles east" },
    { slug: "santa-ynez", name: "Santa Ynez", relationship: "7 miles east" },
    { slug: "santa-barbara", name: "Santa Barbara", relationship: "40 miles southeast" }
  ],
  "santa-ynez": [
    { slug: "solvang", name: "Solvang", relationship: "5 miles west" },
    { slug: "buellton", name: "Buellton", relationship: "7 miles west" },
    { slug: "santa-barbara", name: "Santa Barbara", relationship: "30 miles south" }
  ],
  "isla-vista": [
    { slug: "goleta", name: "Goleta", relationship: "3 miles east" },
    { slug: "santa-barbara", name: "Santa Barbara", relationship: "13 miles east" },
    { slug: "montecito", name: "Montecito", relationship: "18 miles east" }
  ],
  "summerland": [
    { slug: "carpinteria", name: "Carpinteria", relationship: "3 miles southeast" },
    { slug: "montecito", name: "Montecito", relationship: "3 miles northwest" },
    { slug: "santa-barbara", name: "Santa Barbara", relationship: "8 miles northwest" }
  ],
  "oxnard": [
    { slug: "ventura", name: "Ventura", relationship: "8 miles northwest" },
    { slug: "camarillo", name: "Camarillo", relationship: "8 miles northeast" },
    { slug: "carpinteria", name: "Carpinteria", relationship: "30 miles northwest" },
    { slug: "los-angeles", name: "Los Angeles", relationship: "60 miles southeast" }
  ],
  "los-angeles": [
    { slug: "oxnard", name: "Oxnard", relationship: "60 miles northwest" },
    { slug: "ventura", name: "Ventura", relationship: "70 miles northwest" },
    { slug: "camarillo", name: "Camarillo", relationship: "50 miles northwest" }
  ]
};

export default function NearbyCities({ 
  currentCitySlug, 
  serviceSlug, 
  serviceName 
}: NearbyCitiesProps) {
  const nearbyCities = cityRelationships[currentCitySlug] || [];

  const baseUrl = serviceSlug ? `/service/${serviceSlug}` : `/city`;

  // Build full URLs for schema
  const cityUrls = nearbyCities.map(
    (city) => `https://danispainting.net${baseUrl}/${city.slug}`
  );

  // Inject ItemList + significantLink schema
  useEffect(() => {
    if (nearbyCities.length === 0) return;

    const currentCityName = currentCitySlug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());

    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": `${serviceName ? `${serviceName} in ` : "Painting Services in "}Nearby Cities to ${currentCityName}`,
      "description": `${serviceName || "Professional painting services"} available in cities near ${currentCityName}, CA. Serving Santa Barbara and Ventura Counties.`,
      "numberOfItems": nearbyCities.length,
      "itemListElement": nearbyCities.map((city, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": city.name,
        "description": `${serviceName || "Professional painting services"} available in ${city.name} (${city.relationship} from ${currentCityName})`,
        "url": `https://danispainting.net${baseUrl}/${city.slug}`,
        "item": {
          "@type": "City",
          "name": city.name,
          "containedInPlace": {
            "@type": "State",
            "name": "California"
          },
          "url": `https://danispainting.net${baseUrl}/${city.slug}`
        }
      })),
      "significantLink": cityUrls
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(schema);
    script.id = "structured-data-nearby-cities";

    const existing = document.getElementById(script.id);
    if (existing) existing.remove();
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById("structured-data-nearby-cities");
      if (el) el.remove();
    };
  }, [nearbyCities, currentCitySlug, serviceSlug, serviceName, baseUrl, cityUrls]);

  if (nearbyCities.length === 0) return null;

  return (
    <section className="py-16 bg-white">
      <div className="container px-4">
        <div className="text-center mb-12">
          <p className="text-[#1e3a8a] font-semibold mb-2 text-sm tracking-wider">
            SERVING NEARBY AREAS
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0a1628] mb-4">
            {serviceName ? `${serviceName} in ` : ""}Nearby Cities
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            We proudly serve communities throughout Santa Barbara and Ventura Counties
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {nearbyCities.map((city) => (
            <Link
              key={city.slug}
              href={`${baseUrl}/${city.slug}`}
            >
              <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group">
                <CardContent className="p-6">
                  <div className="flex items-start mb-3">
                    <MapPin className="w-5 h-5 text-[#4caf30] mr-2 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-[#0a1628] group-hover:text-[#4caf30] transition-colors">
                        {city.name}
                      </h3>
                      <p className="text-gray-500 text-sm mt-1">
                        {city.relationship}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {serviceName ? `${serviceName} services` : "Professional painting services"} available in {city.name}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
